<!-- src/views/Inventory.vue -->
<template>
  <div class="inventory">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>Inventario</h1>
      <div class="d-flex">
        <button class="btn btn-success me-2" @click="openAddEntryModal">
          <i class="bi bi-plus-circle me-1"></i> Nueva Entrada
        </button>
        <button class="btn btn-primary me-2" @click="openAddExitModal">
          <i class="bi bi-dash-circle me-1"></i> Nueva Salida
        </button>
        <button class="btn btn-outline-secondary me-2" @click="openProductModal">
          <i class="bi bi-box-seam me-1"></i> Nuevo Producto
        </button>
        <button class="btn btn-outline-secondary me-2" @click="openSupplierModal">
          <i class="bi bi-building me-1"></i> Nuevo Proveedor
        </button>
        <button class="btn btn-outline-secondary" @click="refreshData">
          <i class="bi bi-arrow-clockwise me-1"></i> Actualizar
        </button>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <div v-else-if="!hasInventoryData" class="text-center p-5 bg-light rounded">
      <i class="bi bi-box-seam display-1 text-muted mb-3"></i>
      <h3>No hay datos de inventario registrados</h3>
      <p class="text-muted">Inicie su gestión de inventario añadiendo productos y registrando operaciones</p>
    </div>

    <div v-else>
      <!-- Inventory Stats Summary -->
      <div class="row g-4 mb-4">
        <div class="col-md-3">
          <div class="card h-100 bg-light-blue">
            <div class="card-body">
              <h5 class="card-title">Brackets - Stock Total</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-box-seam"></i>
                </div>
                <h2 class="ms-3 mb-0">{{ summary.total_brackets_stock || 0 }}</h2>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card h-100 bg-light-purple">
            <div class="card-body">
              <h5 class="card-title">Mini Implantes - Stock Total</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-bullseye"></i>
                </div>
                <h2 class="ms-3 mb-0">{{ summary.total_mini_implantes_stock || 0 }}</h2>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card h-100 bg-light-green">
            <div class="card-body">
              <h5 class="card-title">Entradas</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-arrow-down-circle"></i>
                </div>
                <div class="ms-3">
                  <h2 class="mb-0">{{ summary.entry_count || 0 }}</h2>
                  <div class="text-success">{{ formatCurrency(summary.total_entries_value) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card h-100 bg-light-orange">
            <div class="card-body">
              <h5 class="card-title">Salidas</h5>
              <div class="d-flex align-items-center">
                <div class="stat-icon">
                  <i class="bi bi-arrow-up-circle"></i>
                </div>
                <div class="ms-3">
                  <h2 class="mb-0">{{ summary.exit_count || 0 }}</h2>
                  <div class="text-danger">{{ formatCurrency(summary.total_exits_value) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Products Table -->
      <div class="card mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Productos</h5>
          <div class="input-group" style="width: 250px;">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Buscar producto..."
              v-model="productSearchTerm"
            >
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Categoría</th>
                  <th>Cantidad Actual</th>
                  <th>Última Actualización</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredProducts.length === 0">
                  <td colspan="5" class="text-center py-3">No hay productos registrados</td>
                </tr>
                <tr v-for="product in filteredProducts" :key="product.id">
                  <td>{{ product.name }}</td>
                  <td>{{ categoryLabel(product.category) }}</td>
                  <td>
                    <span :class="product.current_quantity <= 1 ? 'text-danger fw-bold' : ''">
                      {{ product.current_quantity }}
                    </span>
                  </td>
                  <td>{{ formatDate(product.updated_at) }}</td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button 
                        class="btn btn-outline-success" 
                        @click="openAddEntryModal(product)"
                        title="Añadir Entrada"
                      >
                        <i class="bi bi-plus-circle"></i>
                      </button>
                      <button 
                        class="btn btn-outline-danger" 
                        @click="openAddExitModal(product)"
                        title="Añadir Salida"
                      >
                        <i class="bi bi-dash-circle"></i>
                      </button>
                      <button 
                        class="btn btn-outline-primary" 
                        @click="openProductOperations(product)"
                        title="Ver Operaciones"
                      >
                        <i class="bi bi-list-ul"></i>
                      </button>
                      <button 
                        class="btn btn-outline-secondary" 
                        @click="openEditProductModal(product)"
                        title="Editar"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Operations Table -->
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Operaciones Recientes</h5>
          <div class="input-group" style="width: 250px;">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Buscar operación..."
              v-model="operationSearchTerm"
            >
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Precio Unitario</th>
                  <th>Total</th>
                  <th>Proveedor</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="filteredOperations.length === 0">
                  <td colspan="8" class="text-center py-3">No hay operaciones registradas</td>
                </tr>
                <tr v-for="operation in filteredOperations" :key="operation.id">
                  <td>{{ formatDate(operation.date) }}</td>
                  <td>
                    <span :class="getOperationTypeClass(operation.operation_type)">
                      {{ operation.operation_type_display || getOperationTypeDisplay(operation.operation_type) }}
                    </span>
                  </td>
                  <td>{{ operation.product_name }}</td>
                  <td>{{ operation.quantity }}</td>
                  <td>{{ formatCurrency(operation.unit_price) }}</td>
                  <td>{{ formatCurrency(operation.quantity * operation.unit_price) }}</td>
                  <td>{{ operation.supplier_name || 'N/A' }}</td>
                  <td>
                    <div class="btn-group btn-group-sm">
                      <button 
                        class="btn btn-outline-secondary" 
                        @click="openEditOperationModal(operation)"
                        title="Editar"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button 
                        class="btn btn-outline-danger" 
                        @click="openDeleteOperationModal(operation)"
                        title="Eliminar"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Product Modal -->
    <div class="modal fade" id="productModal" tabindex="-1" aria-hidden="true" ref="productModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditMode ? 'Editar Producto' : 'Nuevo Producto' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form id="productForm" @submit.prevent="saveProduct">
              <div class="mb-3">
                <label for="productName" class="form-label">Nombre del Producto*</label>
                <input
                  type="text"
                  class="form-control"
                  id="productName"
                  v-model="currentProduct.name"
                  required
                >
              </div>
              <div class="mb-3">
                <label for="productCategory" class="form-label">Categoría*</label>
                <select
                  class="form-select"
                  id="productCategory"
                  v-model="currentProduct.category"
                  required
                >
                  <option value="BRACKETS">Brackets</option>
                  <option value="MINI_IMPLANTES">Mini Implantes</option>
                </select>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" class="btn btn-primary" form="productForm">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Supplier Modal -->
    <div class="modal fade" id="supplierModal" tabindex="-1" aria-hidden="true" ref="supplierModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditModeSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form id="supplierForm" @submit.prevent="saveSupplier">
              <div class="mb-3">
                <label for="supplierName" class="form-label">Nombre del Proveedor*</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="supplierName" 
                  v-model="currentSupplier.name"
                  required
                >
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" class="btn btn-primary" form="supplierForm">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Operation Modal -->
    <div class="modal fade" id="operationModal" tabindex="-1" aria-hidden="true" ref="operationModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ getOperationModalTitle() }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form id="operationForm" @submit.prevent="saveOperation">
              <div class="mb-3">
                <label for="operationDate" class="form-label">Fecha*</label>
                <input 
                  type="date" 
                  class="form-control" 
                  id="operationDate" 
                  v-model="currentOperation.date"
                  required
                >
              </div>
              <div class="mb-3">
                <label for="operationProduct" class="form-label">Producto*</label>
                <select 
                  class="form-select" 
                  id="operationProduct" 
                  v-model="currentOperation.product"
                  required
                  :disabled="isEditMode"
                >
                  <option value="" disabled>Seleccione un producto</option>
                  <option v-for="product in products" :key="product.id" :value="product.id">
                    {{ product.name }} (Stock actual: {{ product.current_quantity }})
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label for="operationQuantity" class="form-label">Cantidad*</label>
                <input 
                  type="number" 
                  class="form-control" 
                  id="operationQuantity" 
                  v-model="currentOperation.quantity"
                  min="1"
                  required
                >
                <div v-if="insufficientStock" class="text-danger mt-1">
                  Stock insuficiente para realizar esta salida
                </div>
              </div>
              <div class="mb-3">
                <label for="operationUnitPrice" class="form-label">Precio Unitario*</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input
                    type="number"
                    class="form-control"
                    id="operationUnitPrice"
                    v-model="currentOperation.unit_price"
                    min="0"
                    step="0.01"
                    required
                  >
                </div>
              </div>
              <div class="mb-3">
                <label for="operationSupplier" class="form-label">Proveedor</label>
                <select 
                  class="form-select" 
                  id="operationSupplier" 
                  v-model="currentOperation.supplier"
                >
                  <option value="" selected>Seleccione un proveedor (opcional)</option>
                  <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
                    {{ supplier.name }}
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label for="operationNotes" class="form-label">Notas</label>
                <textarea 
                  class="form-control" 
                  id="operationNotes" 
                  rows="3"
                  v-model="currentOperation.notes"
                ></textarea>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" class="btn btn-primary" form="operationForm" :disabled="insufficientStock">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Operation Modal -->
    <div class="modal fade" id="deleteOperationModal" tabindex="-1" aria-hidden="true" ref="deleteOperationModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>¿Está seguro de que desea eliminar esta operación de inventario?</p>
            <div class="alert alert-warning">
              <div><strong>Fecha:</strong> {{ formatDate(currentOperation.date) }}</div>
              <div><strong>Tipo:</strong> {{ getOperationTypeDisplay(currentOperation.operation_type) }}</div>
              <div><strong>Producto:</strong> {{ currentOperation.product_name }}</div>
              <div><strong>Cantidad:</strong> {{ currentOperation.quantity }}</div>
            </div>
            <p class="text-danger">Esta acción no se puede deshacer y afectará al stock actual del producto.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
            <button type="button" class="btn btn-danger" @click="deleteOperation">Eliminar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Detail Modal -->
    <div class="modal fade" id="productDetailModal" tabindex="-1" aria-hidden="true" ref="productDetailModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Detalle del Producto</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <h4>{{ currentProduct.name }}</h4>
            <div class="row mt-3">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-subtitle mb-2 text-muted">Información General</h6>
                    <p><strong>Stock Actual:</strong> {{ currentProduct.current_quantity }}</p>
                    <p><strong>Última Actualización:</strong> {{ formatDate(currentProduct.updated_at) }}</p>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-subtitle mb-2 text-muted">Estadísticas</h6>
                    <p><strong>Total Entradas:</strong> {{ productStats.totalEntries || 0 }}</p>
                    <p><strong>Total Salidas:</strong> {{ productStats.totalExits || 0 }}</p>
                    <p><strong>Valor Estimado:</strong> {{ formatCurrency(productStats.estimatedValue) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
            <button type="button" class="btn btn-primary" @click="openProductOperations(currentProduct)">
              Ver Operaciones
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Operations Modal -->
    <div class="modal fade" id="productOperationsModal" tabindex="-1" aria-hidden="true" ref="productOperationsModal">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Operaciones del Producto: {{ currentProduct.name }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Cantidad</th>
                    <th>Precio Unitario</th>
                    <th>Total</th>
                    <th>Proveedor</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="productOperations.length === 0">
                    <td colspan="7" class="text-center py-3">No hay operaciones para este producto</td>
                  </tr>
                  <tr v-for="operation in productOperations" :key="operation.id">
                    <td>{{ formatDate(operation.date) }}</td>
                    <td>
                      <span :class="getOperationTypeClass(operation.operation_type)">
                        {{ operation.operation_type_display || getOperationTypeDisplay(operation.operation_type) }}
                      </span>
                    </td>
                    <td>{{ operation.quantity }}</td>
                    <td>{{ formatCurrency(operation.unit_price) }}</td>
                    <td>{{ formatCurrency(operation.quantity * operation.unit_price) }}</td>
                    <td>{{ operation.supplier_name || 'N/A' }}</td>
                    <td>
                      <span v-if="operation.notes && operation.notes.length > 20">
                        {{ operation.notes.substring(0, 20) }}...
                        <i class="bi bi-info-circle text-primary" 
                          role="button" 
                          @click="showOperationNotes(operation)"
                          title="Ver notas completas"></i>
                      </span>
                      <span v-else-if="operation.notes">{{ operation.notes }}</span>
                      <span v-else class="text-muted">Sin notas</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Operation Notes Modal -->
    <div class="modal fade" id="operationNotesModal" tabindex="-1" aria-hidden="true" ref="operationNotesModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Notas de Operación</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <h6>{{ getOperationTypeDisplay(currentOperation.operation_type) }} - {{ formatDate(currentOperation.date) }}</h6>
            <p class="mt-3" style="white-space: pre-line">{{ currentOperation.notes || 'Sin notas' }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { productService, supplierService, inventoryOperationService } from '@/services/api';
import { Modal } from 'bootstrap';

export default {
  name: 'Inventory',
  data() {
    return {
      loading: true,
      products: [],
      suppliers: [],
      operations: [],
      productOperations: [],
      summary: {
        total_products: 0,
        total_brackets_stock: 0,
        total_mini_implantes_stock: 0,
        total_suppliers: 0,
        entry_count: 0,
        exit_count: 0,
        total_entries_value: 0,
        total_exits_value: 0,
        low_inventory_products: 0
      },
      currentProduct: this.getEmptyProduct(),
      currentSupplier: this.getEmptySupplier(),
      currentOperation: this.getEmptyOperation(),
      productStats: {
        totalEntries: 0,
        totalExits: 0,
        estimatedValue: 0
      },
      isEditMode: false,
      isEditModeSupplier: false,
      productSearchTerm: '',
      operationSearchTerm: '',
      // Bootstrap modals
      productModal: null,
      supplierModal: null,
      operationModal: null,
      deleteOperationModal: null,
      productDetailModal: null,
      productOperationsModal: null,
      operationNotesModal: null
    };
  },
  computed: {
    hasInventoryData() {
      return this.products.length > 0 || this.operations.length > 0;
    },
    filteredProducts() {
      if (!this.productSearchTerm) return this.products;
      
      const term = this.productSearchTerm.toLowerCase();
      return this.products.filter(item => 
        item.name.toLowerCase().includes(term)
      );
    },
    filteredOperations() {
      if (!this.operationSearchTerm) return this.operations;
      
      const term = this.operationSearchTerm.toLowerCase();
      return this.operations.filter(item => 
        (item.product_name && item.product_name.toLowerCase().includes(term)) ||
        (item.supplier_name && item.supplier_name.toLowerCase().includes(term)) ||
        (item.notes && item.notes.toLowerCase().includes(term))
      );
    },
    insufficientStock() {
      if (this.currentOperation.operation_type !== 'salida') return false;
      
      const product = this.products.find(p => p.id === this.currentOperation.product);
      if (!product) return false;
      
      // If editing, we need to account for the original quantity
      if (this.isEditMode) {
        const originalOperation = this.operations.find(o => o.id === this.currentOperation.id);
        if (originalOperation && originalOperation.operation_type === 'salida') {
          // If current quantity is the same or less than original, it's ok
          if (parseInt(this.currentOperation.quantity) <= parseInt(originalOperation.quantity)) {
            return false;
          }
          // Otherwise, check if we have enough for the difference
          return (parseInt(this.currentOperation.quantity) - parseInt(originalOperation.quantity)) > product.current_quantity;
        }
      }
      
      return parseInt(this.currentOperation.quantity) > product.current_quantity;
    }
  },
  async created() {
    try {
      await this.refreshData();
    } catch (error) {
      console.error('Error loading inventory data:', error);
      if (this.$toast) {
        this.$toast.error('Error al cargar los datos de inventario');
      } else {
        alert('Error al cargar los datos de inventario');
      }
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    // Initialize Bootstrap modals
    this.productModal = new Modal(this.$refs.productModal);
    this.supplierModal = new Modal(this.$refs.supplierModal);
    this.operationModal = new Modal(this.$refs.operationModal);
    this.deleteOperationModal = new Modal(this.$refs.deleteOperationModal);
    this.productDetailModal = new Modal(this.$refs.productDetailModal);
    this.productOperationsModal = new Modal(this.$refs.productOperationsModal);
    this.operationNotesModal = new Modal(this.$refs.operationNotesModal);
  },
  methods: {
    getEmptyProduct() {
      return {
        id: null,
        name: '',
        category: 'BRACKETS',
        current_quantity: 0
      };
    },
    getEmptySupplier() {
      return {
        id: null,
        name: '',
        contact: '',
        phone: '',
        email: '',
        notes: ''
      };
    },
    getEmptyOperation() {
      return {
        id: null,
        operation_type: 'entrada',
        date: this.formatDateForInput(new Date()),
        product: '',
        quantity: 1,
        unit_price: 0,
        supplier: '',
        notes: ''
      };
    },
    async refreshData() {
      this.loading = true;
      try {
        // Fetch summary data
        const summaryResponse = await inventoryOperationService.getSummary();
        this.summary = summaryResponse.data;

        // Fetch products and suppliers
        const [productsResponse, suppliersResponse, operationsResponse] = await Promise.all([
          productService.getAll(),
          supplierService.getAll(),
          inventoryOperationService.getAll()
        ]);

        this.products = productsResponse.data;
        this.suppliers = suppliersResponse.data;
        this.operations = operationsResponse.data.slice(0, 20); // Show only the last 20 operations
      } catch (error) {
        console.error('Error fetching inventory data:', error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    categoryLabel(category) {
      const labels = {
        BRACKETS: 'Brackets',
        MINI_IMPLANTES: 'Mini Implantes',
      };
      return labels[category] || category || '';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      let date;
      // Full ISO datetime → parse as-is (it has timezone info)
      if (dateString.includes('T') || dateString.includes(' ')) {
        date = new Date(dateString);
      } else {
        // Date-only "YYYY-MM-DD" → anchor at UTC noon to avoid TZ-shift
        date = new Date(dateString + 'T12:00:00Z');
      }
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    formatDateForInput(date) {
      if (!date) return '';
      // Already in YYYY-MM-DD form (e.g. from API) → use as-is so the date
      // is not re-interpreted as UTC midnight and shifted by the local TZ.
      if (typeof date === 'string') {
        const m = date.match(/^(\d{4}-\d{2}-\d{2})/);
        if (m) return m[1];
        date = new Date(date);
      }
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatCurrency(value) {
      if (!value) return '$0.00';
      return '$' + parseFloat(value).toFixed(2);
    },
    getOperationTypeDisplay(type) {
      const types = {
        'entrada': 'Entrada',
        'salida': 'Salida'
      };
      return types[type] || type;
    },
    getOperationTypeClass(type) {
      const classes = {
        'entrada': 'text-success fw-bold',
        'salida': 'text-danger fw-bold'
      };
      return classes[type] || '';
    },
    getOperationModalTitle() {
      if (this.isEditMode) {
        return `Editar ${this.getOperationTypeDisplay(this.currentOperation.operation_type)}`;
      }
      return `Nueva ${this.getOperationTypeDisplay(this.currentOperation.operation_type)}`;
    },
    openProductModal() {
      this.isEditMode = false;
      this.currentProduct = this.getEmptyProduct();
      this.productModal.show();
    },
    openEditProductModal(product) {
      this.isEditMode = true;
      this.currentProduct = { ...product };
      this.productModal.show();
    },
    openSupplierModal() {
      this.isEditModeSupplier = false;
      this.currentSupplier = this.getEmptySupplier();
      this.supplierModal.show();
    },
    openEditSupplierModal(supplier) {
      this.isEditModeSupplier = true;
      this.currentSupplier = { ...supplier };
      this.supplierModal.show();
    },
    openAddEntryModal(product = null) {
      this.isEditMode = false;
      this.currentOperation = this.getEmptyOperation();
      this.currentOperation.operation_type = 'entrada';
      
      if (product) {
        this.currentOperation.product = product.id;
      }
      
      this.operationModal.show();
    },
    openAddExitModal(product = null) {
      this.isEditMode = false;
      this.currentOperation = this.getEmptyOperation();
      this.currentOperation.operation_type = 'salida';
      
      if (product) {
        this.currentOperation.product = product.id;
      }
      
      this.operationModal.show();
    },
    openEditOperationModal(operation) {
      this.isEditMode = true;
      this.currentOperation = { 
        ...operation,
        date: this.formatDateForInput(operation.date)
      };
      this.operationModal.show();
    },
    openDeleteOperationModal(operation) {
      this.currentOperation = { ...operation };
      this.deleteOperationModal.show();
    },
    async showProductDetail(product) {
      this.currentProduct = { ...product };
      
      // Calculate product statistics
      try {
        const response = await productService.getOperations(product.id);
        const operations = response.data;
        
        const entries = operations.filter(op => op.operation_type === 'entrada');
        const exits = operations.filter(op => op.operation_type === 'salida');
        
        this.productStats = {
          totalEntries: entries.reduce((sum, op) => sum + parseInt(op.quantity), 0),
          totalExits: exits.reduce((sum, op) => sum + parseInt(op.quantity), 0),
          estimatedValue: product.current_quantity * (entries.length > 0 ? parseFloat(entries[entries.length - 1].unit_price) : 0)
        };
      } catch (error) {
        console.error('Error fetching product operations:', error);
        this.productStats = {
          totalEntries: 0,
          totalExits: 0,
          estimatedValue: 0
        };
      }
      
      this.productDetailModal.show();
    },
    async openProductOperations(product) {
      this.currentProduct = { ...product };
      this.productOperations = [];
      
      try {
        const response = await productService.getOperations(product.id);
        this.productOperations = response.data.sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
      } catch (error) {
        console.error('Error fetching product operations:', error);
      }
      
      // Close other modals if they're open
      if (this.productDetailModal._isShown) {
        this.productDetailModal.hide();
      }
      
      this.productOperationsModal.show();
    },
    showOperationNotes(operation) {
      this.currentOperation = { ...operation };
      this.operationNotesModal.show();
    },
    async saveProduct() {
      try {
        // Validate required fields
        if (!this.currentProduct.name) {
          if (this.$toast) {
            this.$toast.error('El nombre del producto es obligatorio');
          } else {
            alert('El nombre del producto es obligatorio');
          }
          return;
        }
        
        // Create a copy of the data for sending to the API
        const productData = { ...this.currentProduct };
        
        if (this.isEditMode) {
          await productService.update(productData.id, productData);
          if (this.$toast) {
            this.$toast.success('Producto actualizado con éxito');
          } else {
            alert('Producto actualizado con éxito');
          }
        } else {
          await productService.create(productData);
          if (this.$toast) {
            this.$toast.success('Producto creado con éxito');
          } else {
            alert('Producto creado con éxito');
          }
        }
        
        await this.refreshData();
        this.productModal.hide();
      } catch (error) {
        console.error('Error saving product:', error);
        if (this.$toast) {
          this.$toast.error('Error al guardar el producto');
        } else {
          alert('Error al guardar el producto');
        }
      }
    },
    async saveSupplier() {
      try {
        // Validate required fields
        if (!this.currentSupplier.name) {
          if (this.$toast) {
            this.$toast.error('El nombre del proveedor es obligatorio');
          } else {
            alert('El nombre del proveedor es obligatorio');
          }
          return;
        }
        
        // Create a copy of the data for sending to the API
        const supplierData = { ...this.currentSupplier };
        
        if (this.isEditModeSupplier) {
          await supplierService.update(supplierData.id, supplierData);
          if (this.$toast) {
            this.$toast.success('Proveedor actualizado con éxito');
          } else {
            alert('Proveedor actualizado con éxito');
          }
        } else {
          await supplierService.create(supplierData);
          if (this.$toast) {
            this.$toast.success('Proveedor creado con éxito');
          } else {
            alert('Proveedor creado con éxito');
          }
        }
        
        await this.refreshData();
        this.supplierModal.hide();
      } catch (error) {
        console.error('Error saving supplier:', error);
        if (this.$toast) {
          this.$toast.error('Error al guardar el proveedor');
        } else {
          alert('Error al guardar el proveedor');
        }
      }
    },
    async saveOperation() {
      try {
        // Validate required fields (unit_price === 0 is valid)
        const priceMissing = this.currentOperation.unit_price === '' ||
                             this.currentOperation.unit_price === null ||
                             this.currentOperation.unit_price === undefined;
        if (!this.currentOperation.date || !this.currentOperation.product ||
            !this.currentOperation.quantity || priceMissing) {
          if (this.$toast) {
            this.$toast.error('Por favor complete todos los campos requeridos');
          } else {
            alert('Por favor complete todos los campos requeridos');
          }
          return;
        }
        
        // Check if we have enough stock for a salida
        if (this.insufficientStock) {
          if (this.$toast) {
            this.$toast.error('No hay suficiente stock para realizar esta salida');
          } else {
            alert('No hay suficiente stock para realizar esta salida');
          }
          return;
        }
        
        // Create a copy of the data for sending to the API
        const operationData = { ...this.currentOperation };
        
        // Convert numeric fields
        operationData.quantity = parseInt(operationData.quantity);
        operationData.unit_price = parseFloat(operationData.unit_price);
        
        // If supplier is empty, set it to null
        if (!operationData.supplier) {
          operationData.supplier = null;
        }
        
        if (this.isEditMode) {
          await inventoryOperationService.update(operationData.id, operationData);
          if (this.$toast) {
            this.$toast.success('Operación actualizada con éxito');
          } else {
            alert('Operación actualizada con éxito');
          }
        } else {
          await inventoryOperationService.create(operationData);
          if (this.$toast) {
            this.$toast.success('Operación registrada con éxito');
          } else {
            alert('Operación registrada con éxito');
          }
        }
        
        await this.refreshData();
        this.operationModal.hide();
      } catch (error) {
        console.error('Error saving operation:', error);
        if (this.$toast) {
          this.$toast.error('Error al guardar la operación');
        } else {
          alert('Error al guardar la operación');
        }
      }
    },
    async deleteOperation() {
      try {
        await inventoryOperationService.delete(this.currentOperation.id);
        if (this.$toast) {
          this.$toast.success('Operación eliminada con éxito');
        } else {
          alert('Operación eliminada con éxito');
        }
        
        await this.refreshData();
        this.deleteOperationModal.hide();
      } catch (error) {
        console.error('Error deleting operation:', error);
        if (this.$toast) {
          this.$toast.error('Error al eliminar la operación');
        } else {
          alert('Error al eliminar la operación');
        }
      }
    }
  }
};
</script>

<style scoped>
.inventory {
  padding-bottom: 2rem;
}

.stat-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 1.5rem;
}

.bg-light-blue {
  background-color: rgba(13, 110, 253, 0.1);
}
.bg-light-blue .stat-icon {
  background-color: rgba(13, 110, 253, 0.2);
  color: #0d6efd;
}

.bg-light-green {
  background-color: rgba(25, 135, 84, 0.1);
}
.bg-light-green .stat-icon {
  background-color: rgba(25, 135, 84, 0.2);
  color: #198754;
}

.bg-light-orange {
  background-color: rgba(255, 136, 0, 0.1);
}
.bg-light-orange .stat-icon {
  background-color: rgba(255, 136, 0, 0.2);
  color: #ff8800;
}

.bg-light-purple {
  background-color: rgba(138, 43, 226, 0.1);
}
.bg-light-purple .stat-icon {
  background-color: rgba(138, 43, 226, 0.2);
  color: #8a2be2;
}
</style>